import { Paid } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { PieChart } from '@mui/x-charts';
import { PieValueType } from '@mui/x-charts/models/seriesType/pie';
import React from 'react';
import { ComparisonWithDefault } from '../components/ComparisonWithDefault.tsx';
import { IconWrapper } from '../components/icon/IconWrapper.tsx';
import { BuildingAlternative } from '../stores/SubprojectStore.ts';
import { formattedUSD } from '../utils.ts';

interface DecarbHubCostSectionProps {
  alternative: BuildingAlternative;
  alternativeCompare?: BuildingAlternative;
  getColor: (index: number) => string;
  designCost: number;
  constructionCost: number;
  /**
   * Title for the annual pie. This component is shared: the subproject tile
   * passes "Annual Energy O&M Cost"; the project overview passes "Annual Costs".
   */
  annualCostLabel?: string;
  /**
   * Extra non-energy annual wedges shown on the project overview pie only
   * (e.g. Personnel, Water Utility, and the minor-maintenance delta). Each is
   * appended after the per-system Energy O&M wedges and included in the total.
   */
  extraAnnualWedges?: { label: string; value: number }[];
}

export const DecarbHubCostSection: React.FC<DecarbHubCostSectionProps> = ({
  alternative,
  alternativeCompare,
  getColor,
  designCost,
  constructionCost,
  annualCostLabel = 'Annual Costs',
  extraAnnualWedges,
}) => {
  const costPieSize = 100;

  if (designCost == null || !designCost) {
    designCost = 0;
  }

  if (constructionCost == null || !constructionCost) {
    constructionCost = 0;
  }

  // Early return if alternative is not loaded
  if (!alternative) {
    return null;
  }

  // capital cost by technology
  const capitalCosts = Object.entries(
    alternative?.technologyParams || {},
  ).reduce<PieValueType[]>((a, [_, tech], oidx) => {
    a.push({
      id: oidx,
      // `?? 0`: a failed backend compute stores null figures (and older rows may
      // lack `costs`); never throw or sum NaN here.
      value: tech.costs?.capitalCost ?? 0,
      color: getColor(oidx),
      label: tech.technologyCategory,
    });
    return a;
  }, []);
  capitalCosts.push({
    id: capitalCosts.length,
    value: constructionCost,
    color: getColor(capitalCosts.length),
    label: 'CONSTRUCTION COST',
  });
  // KNOWN OVERLAP -- the DESIGN COST wedge double-counts the passive charge.
  //
  // What is double-counted: when an alternative has at least one passive
  // technology selected, the backend charges 15% of the building's design cost
  // and folds that money into the owning category's total
  // (calculations/orchestration.py Pass 2:
  // `updated_tech_params[category_key]['costs']['capitalCost'] += passive_cap`).
  // That 15% therefore already sits inside the per-category wedge above
  // (HVAC today -- the only category with passive catalog rows), while the
  // wedge below still shows 100% of the design cost. The same dollars appear
  // in two adjacent slices, and the pie over-reads by 15% of design cost per
  // category holding a passive pick.
  //
  // Why it is not fixed here: the API exposes no discrete passive figure -- it
  // is pre-summed into the category total, so there is nothing to subtract.
  // Re-deriving it client-side as `designCost * 0.15 * <categories with a
  // passive pick>` would put a second copy of the stacking rule in the
  // frontend, which is precisely the class of defect the decarb4-passive set
  // exists to remove; it would drift silently the next time the rule changes.
  //
  // What a fix needs: a discrete passive contribution in the serialized
  // alternative (e.g. `costs.passiveCapitalCost`, or a per-category
  // `costs.passiveCapitalCost`), emitted by the same backend code that applies
  // the charge. Then subtract it from this wedge. Tracked as a follow-up to
  // decarb4-passive task 006.
  capitalCosts.push({
    id: capitalCosts.length,
    value: designCost,
    color: getColor(capitalCosts.length),
    label: 'DESIGN COST',
  });

  // Capital pie: one wedge per on-site energy technology (capital cost).
  Object.entries(alternative?.onSiteCosts?.technology || {}).forEach(
    ([key, value]) => {
      capitalCosts.push({
        id: capitalCosts.length,
        value: value.capitalCost,
        color: getColor(capitalCosts.length),
        label: value.name?.toUpperCase() || key,
      });
    },
  );

  // annual energy O&M cost: one wedge per technology category, each wedge =
  // that system's equipment maintenance + its attributed annual energy cost.
  // Energy is now attributed per system on the backend (costs.energyCost), so
  // the old per-source externalEnergyCosts loop is no longer used here.
  const annualCosts = Object.entries(
    alternative?.technologyParams || {},
  ).reduce<PieValueType[]>((a, [_, tech], oidx) => {
    a.push({
      id: oidx,
      value: (tech.costs?.maintenanceCost ?? 0) + (tech.costs?.energyCost ?? 0),
      color: getColor(oidx),
      label: tech.technologyCategory,
    });
    return a;
  }, []);

  // One wedge per on-site energy category (Generators, Solar, Wind, ...), value =
  // that category's O&M. Generation is already credited into the per-system energy
  // cost above, so these wedges are maintenance-only. Shown on both pies.
  Object.entries(alternative?.costs?.onsiteCosts ?? {}).forEach(
    ([category, value]) => {
      annualCosts.push({
        id: annualCosts.length,
        value,
        color: getColor(annualCosts.length),
        label: category,
      });
    },
  );

  // Project overview only: append non-energy operating wedges (Personnel, Water
  // Utility, minor-maintenance delta). Empty on the subproject tile.
  (extraAnnualWedges ?? []).forEach((wedge) => {
    annualCosts.push({
      id: annualCosts.length,
      value: wedge.value,
      color: getColor(annualCosts.length),
      label: wedge.label,
    });
  });

  const totalAnnualCost =
    annualCosts.length === 0
      ? ''
      : annualCosts.reduce<number>((a, b) => a + b.value, 0);

  // comparison annual energy O&M cost: same per-system wedge (maintenance + energy)
  const compareAnnualCosts = Object.entries(
    alternativeCompare?.technologyParams ?? {},
  ).reduce<PieValueType[]>((a, [_, tech], oidx) => {
    a.push({
      id: oidx,
      value: (tech.costs?.maintenanceCost ?? 0) + (tech.costs?.energyCost ?? 0),
      color: getColor(oidx),
      label: tech.technologyCategory,
    });
    return a;
  }, []);
  // On-site energy wedges for the comparison baseline (keeps totals comparable).
  Object.entries(alternativeCompare?.costs?.onsiteCosts ?? {}).forEach(
    ([category, value]) => {
      compareAnnualCosts.push({
        id: compareAnnualCosts.length,
        value,
        color: getColor(compareAnnualCosts.length),
        label: category,
      });
    },
  );
  // Same extra wedges apply to the Conventional baseline, so the annual-cost
  // comparison reflects only the build-driven (energy + equipment) difference.
  (extraAnnualWedges ?? []).forEach((wedge) => {
    compareAnnualCosts.push({
      id: compareAnnualCosts.length,
      value: wedge.value,
      color: getColor(compareAnnualCosts.length),
      label: wedge.label,
    });
  });
  const totalCompareAnnualCost = compareAnnualCosts.reduce<number>(
    (a, b) => a + b.value,
    0,
  );

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant={'h6'} fontSize={'smaller'} fontWeight={600}>
        Cost
      </Typography>
      <Grid container>
        <Grid container size={6}>
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Paid fontSize={'small'} color={'info'} />
            <Typography>Capital Cost</Typography>
          </Grid>
          <Grid
            size={alternativeCompare ? 4 : 12}
            offset={alternativeCompare ? 2 : 0}
            sx={{
              display: 'flex',
              alignItems: alternativeCompare ? 'flex-start' : undefined,
              justifyContent: alternativeCompare ? undefined : 'center',
            }}
          >
            {/* A plain sum: a truthiness ternary here dropped on-site capital
                whenever building-tech capital was 0 (an energy-only
                alternative), so the tile disagreed with the pie. */}
            {formattedUSD(
              Number(alternative?.costs?.capitalCost ?? 0) +
                Number(constructionCost) +
                Number(designCost) +
                Number(alternative?.onSiteCosts?.capitalCost ?? 0),
            )}
          </Grid>
          {alternativeCompare && (
            <Grid
              size={4}
              textAlign={'center'}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <ComparisonWithDefault
                value={alternative?.costs?.capitalCost || 0}
                compare={alternativeCompare?.costs?.capitalCost}
                sx={{ fontWeight: 'normal' }}
              />
            </Grid>
          )}
          <Grid
            size={12}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <PieChart
              height={costPieSize}
              width={costPieSize}
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                '*': {
                  stroke: `transparent !important`,
                },
              }}
              series={[
                {
                  valueFormatter: (v) => formattedUSD(v.value),
                  cx: costPieSize / 2 - 5,
                  cy: costPieSize / 2 - 5,
                  outerRadius: costPieSize / 2,
                  sortingValues: 'asc',
                  data: capitalCosts,
                },
              ]}
            />
          </Grid>
        </Grid>
        <Grid container size={6}>
          <Grid
            size={12}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <IconWrapper color={'info'} icon={'AnnualCost'} />
            <Typography>{annualCostLabel}</Typography>
          </Grid>
          <Grid
            size={alternativeCompare ? 4 : 12}
            offset={alternativeCompare ? 2 : 0}
            sx={{
              display: 'flex',
              alignItems: alternativeCompare ? 'flex-start' : undefined,
              justifyContent: alternativeCompare ? undefined : 'center',
            }}
          >
            {totalAnnualCost === '' ? '---' : formattedUSD(totalAnnualCost)}
          </Grid>
          {alternativeCompare && (
            <Grid
              size={4}
              textAlign={'center'}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'flex-end',
                gap: 1,
              }}
            >
              <ComparisonWithDefault
                value={totalAnnualCost === '' ? 0 : totalAnnualCost}
                compare={totalCompareAnnualCost}
                sx={{
                  fontWeight: 'normal',
                }}
              />
            </Grid>
          )}
          <Grid size={12} sx={{ display: 'flex', alignItems: 'center' }}>
            <PieChart
              height={costPieSize}
              width={costPieSize}
              slotProps={{
                legend: { hidden: true },
              }}
              sx={{
                '*': {
                  stroke: 'transparent !important',
                },
              }}
              series={[
                {
                  valueFormatter: (v) => formattedUSD(v.value),
                  cx: costPieSize / 2 - 5,
                  cy: costPieSize / 2 - 5,
                  outerRadius: costPieSize / 2,
                  data: annualCosts,
                },
              ]}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
